// High-fidelity DOCX → HTML for the document-template wizard.
//
// WHY NOT MAMMOTH: the previous converter (mammoth) is a *semantic* converter — by design it
// maps Word styles to clean HTML elements and DISCARDS visual formatting (fonts, sizes, colors,
// margins, table borders/shading, alignment, positioning). The stored HTML therefore looked
// nothing like the source .docx, and the PDF rendered from it (a screenshot of that HTML) was
// just as plain. docx-preview instead reproduces Word's actual layout — it reads the OOXML and
// emits HTML + a scoped <style> block that mirrors the document's appearance.
//
// Everything runs in the browser, so the browser's own engine shapes Arabic, runs the bidi
// algorithm and wraps lines; that correctness carries through when utils/htmlToPdf.ts later
// screenshots the rendered DOM into the PDF.

// docx-preview NATIVE PAGE MODE — reproduce Word's true A4 page geometry so the preview and the
// PDF match Microsoft Word almost pixel-for-pixel:
//  - inWrapper:true       → docx-preview emits real `.docx-wrapper > section.docx` A4 page boxes
//                           with the page margins as padding, a positioned header/footer band, and
//                           the background/watermark layer — the grey "desk" wrapper is neutralized
//                           by DOCX_FIX_STYLES so the page still reads as clean white.
//  - ignoreWidth/Height:false → keep Word's real page size + margins (so the text column width and
//                           therefore the line breaks match Word, instead of reflowing at a fixed
//                           794px which widened every line).
//  - breakPages:true      → real page boxes; htmlToPdf screenshots EACH `section.docx` into its own
//                           A4 PDF page (no blind slicing of one tall image).
//  - useBase64URL:true    → inline images as data: URIs so the stored HTML is self-contained
//                           (no blob: URLs that die once the wizard tab is gone).
//  - experimental:true    → enables tab-stop / layout features for closer fidelity.
//  - ignoreLastRenderedPageBreak:false → honour Word's saved `w:lastRenderedPageBreak` markers so a
//                           multi-page Word document splits into one `section.docx` per page, each with
//                           its OWN header/footer/watermark (Word repeats them on every page). htmlToPdf
//                           screenshots each section into its own A4 page. Explicit `<w:br w:type="page"/>`
//                           already split regardless of this flag; markers cover the far more common case
//                           of a doc that Word paginated by ordinary text overflow. Docs with NO markers
//                           at all still come back as one over-tall section — htmlToPdf's band-repeat path
//                           (utils/docxPdfRender.ts) re-stamps the header/footer/watermark on every page.
const DOCX_PREVIEW_OPTIONS = {
  className: 'docx',
  inWrapper: true,
  ignoreWidth: false,
  ignoreHeight: false,
  breakPages: true,
  ignoreLastRenderedPageBreak: false,
  experimental: true,
  trimXmlDeclaration: true,
  useBase64URL: true,
  renderHeaders: true,
  renderFooters: true,
  renderFootnotes: true,
  renderEndnotes: true,
};

// docx-preview emits `--docx-majorHAnsi-font` / `--docx-minorHAnsi-font` from the theme, but NEVER
// the complex-script (Bidi) or East-Asian slots — so a run that references them (very common: a
// theme where the ascii font itself IS the *Bidi theme slot) resolves to the wrong latin font and
// reflows. Word falls back to Times New Roman for an EMPTY complex-script theme slot; mirror that,
// while honoring a real theme font when one is specified. Keyed off the parsed theme, so it
// generalizes to any document (no hardcoding).
function themeFontVarsCss(doc: unknown): string {
  const scheme = (doc as { themePart?: { theme?: { fontScheme?: {
    majorFont?: { csTypeface?: string; eaTypeface?: string };
    minorFont?: { csTypeface?: string; eaTypeface?: string };
  } } } })?.themePart?.theme?.fontScheme;
  if (!scheme) return '';
  const q = (v?: string) => (v && v.trim() ? `"${v.trim()}"` : '');
  const majCs = q(scheme.majorFont?.csTypeface);
  const minCs = q(scheme.minorFont?.csTypeface);
  const majEa = q(scheme.majorFont?.eaTypeface);
  const minEa = q(scheme.minorFont?.eaTypeface);
  return (
    '.docx{' +
    `--docx-majorBidi-font:${majCs || '"Times New Roman"'};` +
    `--docx-minorBidi-font:${minCs || '"Times New Roman"'};` +
    (majEa ? `--docx-majorEastAsia-font:${majEa};` : '') +
    (minEa ? `--docx-minorEastAsia-font:${minEa};` : '') +
    '}'
  );
}

const RENDER_HOST_WIDTH_PX = 794; // A4 content width == the native `section.docx` width; dir/width mirror the render hosts

// Word interprets a paragraph's `lineRule="auto"` `line=N` spacing as (N/240) * the FONT's natural line
// height, but docx-preview emits it as a UNITLESS CSS `line-height` multiple (N/240), which CSS applies
// to the em box (natural factor 1.0). The result: every auto-spaced line is too short, so the whole
// document is vertically compressed vs Word and blocks drift progressively upward down the page.
// Multiplying the unitless line-heights back up by the font's natural factor restores Word's vertical
// rhythm. 1.22 is the calibrated factor for the serif body this feature renders (Times New Roman's
// nominal natural line height is ~1.15; Word's print/PDF line pitch — plus its paragraph-spacing model
// across the empty spacer lines — renders a touch looser). Measured against a Word reference it lands
// every block within a few px top-to-bottom; see tests/e2e/scripts/docx-fidelity-harness.spec.ts.
const DOCX_AUTO_LINE_FACTOR = 1.22;

// Scale ONLY unitless line-heights (docx-preview's auto-rule multiples) by the natural factor. Exact /
// atLeast line rules are emitted with a `pt` unit and `normal` has no number, so both are left untouched.
export function correctAutoLineHeights(html: string): string {
  return html.replace(/line-height:\s*(\d+(?:\.\d+)?)(\s*[;}"'])/g, (_m, num: string, tail: string) =>
    `line-height:${(parseFloat(num) * DOCX_AUTO_LINE_FACTOR).toFixed(4)}${tail}`);
}

// Matches a single, well-formed "{{placeholder}}" (no nested braces).
const PLACEHOLDER = /\{\{([^{}]+)\}\}/g;

// A placeholder typed as one uniformly-formatted Word run survives as contiguous text. But if
// Word split it across runs (manual formatting, spell-check, tracked edits), docx-preview emits
// it across sibling <span>s — e.g. `<span>{{</span><span>name</span><span>}}</span>`. The server
// fills placeholders with a plain-text regex over the HTML, so a token broken by tags would never
// match. This re-joins such tokens by stripping only the tags BETWEEN the braces, leaving all
// surrounding markup intact. Well-authored (single-run) placeholders contain no inner tags and
// are left untouched, so this is a no-op for them and a repair for the rest.
function joinSplitPlaceholders(html: string): string {
  // `(?:[^{}]|<[^>]*>)*?` — lazily consume text and tags but never another brace, so a match can
  // never run past its own closing `}}` into a later placeholder.
  return html.replace(/\{\{(?:[^{}]|<[^>]*>)*?\}\}/g, (token) => token.replace(/<[^>]*>/g, ''));
}

// ============================================================================
// Recover content docx-preview 0.4.0 DROPS during parse, straight from the source OOXML.
// docx-preview silently discards two Word constructs this feature needs, and both survive ONLY in the
// .docx parts (they are gone from the rendered DOM), so we re-open the zip here (jszip is docx-preview's
// own dependency, already installed) and annotate the LIVE rendered DOM before it is serialized — so the
// repair is baked into the stored template HTML and drives BOTH the wizard preview and the PDF (WYSIWYG).
//   • Gap 1 — VML <v:textpath> WordArt / TEXT watermarks (a diagonal "CONFIDENTIAL"/"DRAFT"): docx-preview's
//     VML parser has no case for <v:textpath>, so it renders the shape as an EMPTY behind-text
//     <svg><g/></svg> — the glyph string/fill/rotation never reach the DOM (image watermarks are fine). We
//     read them from the XML and paint an SVG <text> INTO that same empty svg, preserving its negative
//     z-index / mso-position / geometry so the existing washout fade, layout centering and PDF band-repeat
//     all keep working unchanged.
//   • Gap 2 — PAGE / NUMPAGES fields in headers/footers: field runs render to a bare <span> carrying the
//     STATIC number Word last cached, with NO identifying attribute — indistinguishable from label spans, so
//     the band-repeat PDF path (docxPdfRender.ts) can't renumber them per page and they freeze at "1 of N" on
//     every page. We correlate the source runs to the emitted spans (docx-preview drops only the field-control
//     runs, so renderable runs ↔ spans are 1:1 in order) and mark each field span with data-mzf-pagefield.
// Best-effort throughout: the conversion has already succeeded, so any parsing failure is swallowed and
// simply leaves that document rendering exactly as before.
// ============================================================================

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const VML_NS = 'urn:schemas-microsoft-com:vml';
const SVG_NS = 'http://www.w3.org/2000/svg';
const PX_PER_PT = 96 / 72;
// Word washes a text watermark to a light grey. The pipeline's 0.12 image-watermark washout (docxFixStyles)
// would make a light FILL nearly invisible (it mathematically cannot reach a mid grey from any colour over
// white), so we paint the glyphs at this opacity instead — faint enough to sit behind the body text, dark
// enough to read like Word's diagonal wash. Set inline, so it beats the 0.12 stylesheet rule; the PDF
// band-repeat re-applies getComputedStyle().opacity, so preview and PDF stay in sync.
const TEXT_WATERMARK_OPACITY = '0.5';

// Read a single declaration value out of an inline/style string (handles quotes; no layout needed).
function cssProp(style: string, prop: string): string {
  const m = new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+)`, 'i').exec(style);
  return m ? m[1].trim().replace(/^["']|["']$/g, '') : '';
}
function ptToPx(v: string): number {
  const m = /(-?\d+(?:\.\d+)?)\s*pt/i.exec(v);
  return m ? parseFloat(m[1]) * PX_PER_PT : 0;
}

interface TextWatermark { text: string; fill: string; fontFamily: string; rotationDeg: number; geomKey: string; }

// Collect every VML textpath shape (Word text watermark) from the given parts, in document order.
function parseTextWatermarks(parts: Document[]): TextWatermark[] {
  const out: TextWatermark[] = [];
  for (const doc of parts) {
    for (const shape of Array.from(doc.getElementsByTagNameNS(VML_NS, 'shape'))) {
      const tp = shape.getElementsByTagNameNS(VML_NS, 'textpath')[0];
      const text = tp?.getAttribute('string');
      if (!tp || !text || !text.trim()) continue;
      const style = shape.getAttribute('style') || '';
      let rot = parseFloat(cssProp(style, 'rotation')) || 0;
      rot = ((rot % 360) + 360) % 360; if (rot > 180) rot -= 360; // VML clockwise degrees → [-180, 180]
      out.push({
        text,
        fill: shape.getAttribute('fillcolor') || '#c0c0c0',
        fontFamily: cssProp(tp.getAttribute('style') || '', 'font-family') || 'Arial',
        rotationDeg: rot,
        // Geometry signature to match a source shape to its rendered svg (docx-preview copies the shape's
        // style verbatim onto the svg), robust to shape order; falls back to order when it can't match.
        geomKey: `${cssProp(style, 'width')}|${cssProp(style, 'height')}|${cssProp(style, 'z-index')}|${cssProp(style, 'rotation')}`,
      });
    }
  }
  return out;
}

// Paint the parsed watermark strings into the empty behind-text svgs docx-preview produced.
function paintTextWatermarks(watermarks: TextWatermark[], bodyEl: HTMLElement): void {
  if (watermarks.length === 0) return;
  const emptySvgs = (Array.from(bodyEl.querySelectorAll('svg')) as unknown as SVGSVGElement[]).filter((svg) => {
    const s = (svg.getAttribute('style') || '').replace(/\s+/g, '');
    return s.includes('z-index:-') && !svg.querySelector('text'); // behind-text drawing, not yet painted
  });
  const used = new Array(watermarks.length).fill(false);
  for (const svg of emptySvgs) {
    const style = svg.getAttribute('style') || '';
    const key = `${cssProp(style, 'width')}|${cssProp(style, 'height')}|${cssProp(style, 'z-index')}|${cssProp(style, 'rotation')}`;
    let i = watermarks.findIndex((w, idx) => !used[idx] && w.geomKey === key);
    if (i < 0) i = used.findIndex((u) => !u); // no geometry match → next unused shape, in order
    if (i < 0) break;
    used[i] = true;
    injectWatermarkText(svg, watermarks[i]);
  }
}

function injectWatermarkText(svg: SVGSVGElement, wm: TextWatermark): void {
  const style = svg.getAttribute('style') || '';
  const w = ptToPx(cssProp(style, 'width')) || 553; // fallback ≈ 415pt if the inline size is missing
  const h = ptToPx(cssProp(style, 'height')) || 276;
  // docx-preview zeroed the svg width/height attributes (getBBox on the empty <g>); restore them + a viewBox
  // so the user-unit coordinate space matches the box the inline pt size gives the svg on screen.
  svg.setAttribute('width', String(Math.round(w)));
  svg.setAttribute('height', String(Math.round(h)));
  svg.setAttribute('viewBox', `0 0 ${Math.round(w)} ${Math.round(h)}`);
  const cx = w / 2, cy = h / 2;
  // Word's WordArt "fitshape" scales the string to fill the box; approximate by sizing to ~92% of the width
  // (average glyph advance ≈ 0.58em), capped so it never exceeds the box height.
  const fontSize = Math.max(8, Math.min((0.92 * w) / (0.58 * Math.max(1, wm.text.length)), h * 0.9));
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', String(cx));
  text.setAttribute('y', String(cy));
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('font-family', wm.fontFamily);
  text.setAttribute('font-size', String(Math.round(fontSize)));
  text.setAttribute('font-weight', 'bold');
  text.setAttribute('fill', wm.fill);
  if (wm.rotationDeg) text.setAttribute('transform', `rotate(${wm.rotationDeg} ${cx} ${cy})`);
  text.textContent = wm.text;
  (svg.querySelector('g') || svg).appendChild(text);
  svg.style.opacity = TEXT_WATERMARK_OPACITY;
}

// Build the ordered list of RENDERABLE runs for a header/footer part, flagging which are the value of a
// PAGE / NUMPAGES field. docx-preview drops only the field-control runs (fldChar/instrText) and renders every
// other run as one <span> in order, so this list lines up 1:1 with the emitted band's spans.
function parseBandFieldTokens(doc: Document): { text: string; field: 'PAGE' | 'NUMPAGES' | null }[] {
  const tokens: { text: string; field: 'PAGE' | 'NUMPAGES' | null }[] = [];
  let active: 'PAGE' | 'NUMPAGES' | null = null;  // between fldChar separate .. end
  let pending: 'PAGE' | 'NUMPAGES' | null = null; // instruction seen between begin .. separate
  for (const r of Array.from(doc.getElementsByTagNameNS(W_NS, 'r'))) {
    const fldChar = r.getElementsByTagNameNS(W_NS, 'fldChar')[0];
    if (fldChar) {
      const type = fldChar.getAttributeNS(W_NS, 'fldCharType') || fldChar.getAttribute('w:fldCharType') || '';
      if (type === 'begin') { pending = null; active = null; }
      else if (type === 'separate') { active = pending; }
      else if (type === 'end') { active = null; pending = null; }
      continue; // control run — docx-preview does not render it
    }
    const instrText = r.getElementsByTagNameNS(W_NS, 'instrText')[0];
    if (instrText) {
      const instr = (instrText.textContent || '').toUpperCase();
      if (/\bNUMPAGES\b/.test(instr)) pending = 'NUMPAGES';
      else if (/\bPAGE\b/.test(instr)) pending = 'PAGE';
      continue; // control run — docx-preview does not render it
    }
    const hasVisible = r.getElementsByTagNameNS(W_NS, 't').length
      || r.getElementsByTagNameNS(W_NS, 'br').length
      || r.getElementsByTagNameNS(W_NS, 'tab').length;
    if (!hasVisible) continue; // a run with no renderable child emits no span
    const runText = Array.from(r.getElementsByTagNameNS(W_NS, 't')).map((t) => t.textContent || '').join('');
    tokens.push({ text: runText, field: active });
  }
  return tokens;
}

// Mark each PAGE/NUMPAGES field span so the PDF band-repeat renderer can renumber it per page. Self-correlates
// each source part to its rendered band by matching the run-text sequence (no relationship-part parsing).
function tagPageFields(parts: Document[], bodyEl: HTMLElement): void {
  const partTokens = parts.map(parseBandFieldTokens);
  if (!partTokens.some((t) => t.some((tok) => tok.field))) return; // no page fields anywhere
  const used = new Array(partTokens.length).fill(false);
  for (const band of Array.from(bodyEl.querySelectorAll('header, footer')) as HTMLElement[]) {
    // Leaf spans (no nested span) carry the run text, in document order.
    const spans = (Array.from(band.querySelectorAll('span')) as HTMLElement[]).filter((s) => !s.querySelector('span'));
    const i = partTokens.findIndex((toks, idx) =>
      !used[idx]
      && toks.some((tok) => tok.field)
      && toks.length === spans.length
      && toks.every((tok, k) => tok.text === (spans[k].textContent || '')));
    if (i < 0) continue; // no exact-sequence match → leave this band untagged (graceful)
    used[i] = true;
    partTokens[i].forEach((tok, k) => { if (tok.field) spans[k].setAttribute('data-mzf-pagefield', tok.field); });
  }
}

// Single OOXML pass: recover the two dropped constructs (Gap 1 watermark glyphs, Gap 2 page-field tags).
async function annotateDroppedOoxmlContent(arrayBuffer: ArrayBuffer, bodyEl: HTMLElement): Promise<void> {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(arrayBuffer);
    const parser = new DOMParser();
    const parse = async (name: string): Promise<Document | null> => {
      const f = zip.files[name];
      if (!f) return null;
      return parser.parseFromString(await f.async('string'), 'application/xml');
    };
    const names = Object.keys(zip.files);
    const wmDocs = (await Promise.all(
      names.filter((n) => /^word\/(header\d*|footer\d*|document)\.xml$/i.test(n)).map(parse),
    )).filter(Boolean) as Document[];
    paintTextWatermarks(parseTextWatermarks(wmDocs), bodyEl);

    const hfDocs = (await Promise.all(
      names.filter((n) => /^word\/(header|footer)\d*\.xml$/i.test(n)).map(parse),
    )).filter(Boolean) as Document[];
    tagPageFields(hfDocs, bodyEl);
  } catch {
    // Best-effort: annotation must never fail an already-successful conversion.
  }
}

export interface DocxConversion {
  // Self-contained HTML: a scoped <style> block followed by the rendered document, with images
  // inlined as base64. Safe to persist and re-render later.
  html: string;
  // Distinct "{{placeholder}}" names found in the document body (for the wizard's preview chips;
  // the server re-derives the authoritative list from the stored HTML).
  detectedFields: string[];
}

export async function convertDocxToHtml(arrayBuffer: ArrayBuffer): Promise<DocxConversion> {
  // Code-split: the renderer (~74 KB min + jszip) only loads when a file is actually uploaded.
  const docx = await import('docx-preview');
  // Corrective styles + the render-time geometry pass are code-split with the converter.
  const { DOCX_FIX_STYLES } = await import('./docxFixStyles');

  // Render off-screen but ATTACHED — docx-preview measures element layout (tab stops, widths),
  // and the <style> block must be live in the document for those measurements to be correct. The
  // host dir mirrors the render hosts (preview + htmlToPdf) so bidi resolution is consistent.
  const host = document.createElement('div');
  host.setAttribute('dir', 'auto');
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${RENDER_HOST_WIDTH_PX}px;`;
  const styleEl = document.createElement('div');
  const bodyEl = document.createElement('div');
  host.appendChild(styleEl);
  host.appendChild(bodyEl);
  document.body.appendChild(host);

  try {
    // renderAsync returns the parsed WordDocument, so we can read the theme without re-parsing.
    const doc = await docx.renderAsync(arrayBuffer, bodyEl, styleEl, DOCX_PREVIEW_OPTIONS);

    // Append the theme complex-script font vars + corrective CSS AFTER docx-preview's own <style>
    // blocks so they win the cascade. This is persisted with the document, so the SAME styles feed
    // the wizard preview and the PDF (WYSIWYG). Geometry centering is NOT baked here — it runs at
    // render time (utils/docxLayoutFix.ts) where it can measure the real display context.
    const fixStyle = document.createElement('style');
    fixStyle.textContent = `${themeFontVarsCss(doc)}\n${DOCX_FIX_STYLES}`;
    styleEl.appendChild(fixStyle);

    // Detect from the rendered body text only (not the CSS in styleEl); textContent concatenates
    // across runs, so split placeholders are already contiguous here.
    const detectedFields = Array.from(
      new Set(
        Array.from((bodyEl.textContent || '').matchAll(PLACEHOLDER))
          .map((m) => m[1].trim())
          .filter((s) => s.length > 0),
      ),
    );

    // Recover content docx-preview drops (text-watermark glyphs + page-number field identity) from the
    // source OOXML and annotate the live DOM before serialization. Wait two frames first so docx-preview's
    // own post-render VML measurement pass (which zeroes the empty watermark svg) has finished — otherwise it
    // would clobber the geometry we set on the injected watermark text. Best-effort; never blocks conversion.
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    await annotateDroppedOoxmlContent(arrayBuffer, bodyEl);

    // Persist the scoped styles together with the document so it re-renders faithfully anywhere.
    // correctAutoLineHeights restores Word's auto line-rule spacing (see above) — baked in so the
    // SAME bytes drive the wizard preview and the PDF.
    const html = correctAutoLineHeights(joinSplitPlaceholders(styleEl.innerHTML + bodyEl.innerHTML));
    return { html, detectedFields };
  } finally {
    document.body.removeChild(host);
  }
}
